import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useCart } from "./CartContext";
import Popup from "../components/Popup";
import StickyTableHeader from "../components/utility/StickyTableHeader";
import tablehead from "../include/tablehead.json";
import AddToCard from "../components/utility/AddToCard";
import QuantityControl from "../components/utility/QuantityControl";
import plus from "../assets/img/plus.png";
import minus from "../assets/img/minus.png";
import SingleCategory from "../components/utility/SingleCategory";
import config from "../config";
import { useCurrency } from "../components/utility/CurrencyContext";
import "./table.css";
import ProductTable from "../utils/ProductTable/ProductTable";

const Table2 = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const { categoryId } = useParams();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [counters, setCounters] = useState({});
  const [filters, setFilters] = useState({});
  const [open, setOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { addToCart } = useCart();
  const [showFilters, setShowFilters] = useState(false);

  // New state for pagination and selection
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [priceDisplay, setPriceDisplay] = useState(() => {
    const savedPriceDisplay = localStorage.getItem("priceDisplay");
    return savedPriceDisplay ? savedPriceDisplay : "per100";
  });

  // Effect to save priceDisplay to local storage whenever it changes
  useEffect(() => {
    localStorage.setItem("priceDisplay", priceDisplay);
  }, [priceDisplay]);

  const handlePriceDisplayChange = (displayType) => {
    setPriceDisplay(displayType);
  };

  const [selectedItems, setSelectedItems] = useState([]);
  const navigate = useNavigate();

  const { currency, setCurrency, convertPrice, getCurrencySymbol, setUnit } =
    useCurrency();

  // table toggle
  const [expandedRow, setExpandedRow] = useState(null);
  const toggleExpandRow = (productId) => {
    setExpandedRow((prev) => (prev === productId ? null : productId));
  };

  const handleCurrencyChange = (event) => {
    setCurrency(event.target.value);
  };

  // const openPopup = (product, quantity) => {
  //   const { price } = getPriceForQuantity(product, quantity);
  //   setSelectedProduct({ ...product, resolvedPrice: price });
  //   // addToCart({ ...product, resolvedPrice: price }, quantity);
  //   addToCart({ ...product, priceDisplay }, actualQuantity);
  //   setOpen(true);
  // };

  // const openPopup = (product, quantity) => {
  //   const { price } = getPriceForQuantity(product, quantity);
  //   setSelectedProduct({ ...product, resolvedPrice: price });
  //   addToCart({ ...product, resolvedPrice: price }, quantity);   // ← adds AGAIN
  //   setOpen(true);
  // };
  const openPopup = (product, quantity) => {
    const storedQty = priceDisplay === "per100" ? quantity * 100 : quantity;
    const { price } = getPriceForQuantity(product, storedQty);
    setSelectedProduct({ ...product, resolvedPrice: price });
    setOpen(true);
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(
          `${config.baseURL}/productsfront/${categoryId}`
        );
        if (!response.ok) throw new Error("Failed to fetch products");
        const data = await response.json();
        setProducts(data);
        setFilteredProducts(data);

        const initialCounters = {};
        data.forEach((product) => {
          initialCounters[product.id] = 1;
        });
        setCounters(initialCounters);
      } catch (error) {
        console.error(error);
        alert("An error occurred while fetching products.");
      }
    };
    fetchProducts();
  }, [categoryId]);

  const handleFilterChange = (column, value) => {
    setFilters((prev) => ({
      ...prev,
      [column]: value,
    }));
  };

  useEffect(() => {
    let filteredData = products;

    Object.entries(filters).forEach(([column, value]) => {
      if (value !== "") {
        filteredData = filteredData.filter(
          (item) => String(item[column]) === String(value)
        );
      }
    });

    setFilteredProducts(filteredData);
  }, [filters, products]);

  const increment = (id) => {
    setCounters((prev) => ({
      ...prev,
      [id]: (prev[id] || 1) + 1,
    }));
  };

  const decrement = (id) => {
    setCounters((prev) => ({
      ...prev,
      [id]: prev[id] > 1 ? prev[id] - 1 : 1,
    }));
  };

  const handleInputChange = (e, productId) => {
    const newValue = parseInt(e.target.value, 10);
    if (!isNaN(newValue) && newValue > 0) {
      setCounters((prev) => ({
        ...prev,
        [productId]: newValue,
      }));
    }
  };

  const resetFilters = () => {
    setFilters({});
    setFilteredProducts(products);
    const dropdowns = document.querySelectorAll("select");
    dropdowns.forEach((dropdown) => {
      dropdown.value = "";
    });
  };

  const searchedProducts = filteredProducts.filter((product) =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(searchedProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = searchedProducts.slice(startIndex, endIndex);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(currentProducts.map((product) => product.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (productId) => {
    setSelectedItems((prev) => {
      if (prev.includes(productId)) {
        return prev.filter((id) => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const addSelectedToCart = () => {
    selectedItems.forEach((productId) => {
      const product = products.find((p) => p.id === productId);
      if (product) {
        addToCart(product, counters[productId] || 1);
      }
    });
    setOpen(true);
  };

  // Price calculation
  const calculatePrice = (price) => {
    const convertedPrice = convertPrice(price); // per-100 value
    return priceDisplay === "single" ? convertedPrice / 100 : convertedPrice;
  };

  // Toggle filters visibility on mobile
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // shop now
  const shopnow = (productId) => {
    navigate(`/shopnow/${productId}`);
  };

  // Table2.js

  // Determine the correct price field based on quantity
  const getPriceForQuantity = (product, quantity) => {
    const qty = Number(quantity) || 1;

    if (qty >= 10000 && product.price_10000plus) {
      return { price: product.price_10000plus, tier: "10000+" };
    }
    if (qty >= 5000 && product.price_5000_9999) {
      return { price: product.price_5000_9999, tier: "5000-9999" };
    }
    if (qty >= 1000 && product.price_1000_4999) {
      return { price: product.price_1000_4999, tier: "1000-4999" };
    }
    if (qty >= 250 && product.price_250_999) {
      return { price: product.price_250_999, tier: "250-999" };
    }
    if (product.price_1_249) {
      return { price: product.price_1_249, tier: "1-249" };
    }
    return { price: null, tier: "POA" };
  };
  return (
    <>
      <Popup open={open} setOpen={setOpen} product={selectedProduct} />
      <div className="relative isolate px-3 sm:px-12 pt-0 ">
        <div className="mx-auto max-w-full py-3 sm:pb-5">
          {/* Mobile filter toggle button */}
          <h1 className="text-3xl font-bold mb-6 text-[#333333] text-start">
            Internal Circlip DIN 472 Metric
          </h1>
          <div className="block sm:hidden mb-4">
            <button
              onClick={toggleFilters}
              className="w-full bg-[#EF3F33] text-white py-3 px-4 rounded-lg text-start font-semibold"
            >
              {showFilters ? "Hide Filters" : "Filter Options"}
            </button>
          </div>

          <div className="flex flex-col sm:flex-row sm:space-x-5 mt-4">
            {/* Filters section with responsive changes */}
            <div
              className={`${showFilters ? "block" : "hidden"
                } sm:block w-full sm:w-[19%] h-auto p-4 bg-[#707070] mb-4 sm:mb-0`}
            >
              <p className="font-white font-thin text-[35px] text-white">
                Product Filters
              </p>
              <h2 className="text-lg font-semibold mb-4 text-white">
                Material
              </h2>
              <div className="flex flex-col gap-3 mb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <button className="bg-[#ef3f33] text-white text-[1.0631rem] hover:bg-[#81d742] px-1 py-2 font-medium rounded text-sm">
                    Carbon Spring Steel
                  </button>
                  <p className="text-[#c3bbbb] font-medium">(125)</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button className="bg-[#ef3f33] text-white text-[1.0631rem] hover:bg-[#81d742] p-2 font-medium rounded  text-sm">
                    Stainless Steel
                  </button>
                  <p className="text-[#c3bbbb] font-medium">(43)</p>
                </div>
              </div>

              {/* Bore Filter */}
              <label className="block mb-2 text-white text-base leading-5 font-bold">
                Bore (mm):
              </label>
              <select
                className="w-full p-2 border rounded mb-4 text-sm text-[#999]"
                onChange={(e) => handleFilterChange("bore", e.target.value)}
              >
                <option value="">Any Bore</option>
                {[...new Set(products.map((p) => p.bore))].map((bore) => (
                  <option key={bore} value={bore}>
                    {bore}
                  </option>
                ))}
              </select>

              {/* Thickness Filter */}
              <label className="block mb-2 text-base leading-5 font-bold text-white">
                Thick (mm):
              </label>
              <select
                className="w-full p-2 border rounded mb-4 text-sm text-[#999]"
                onChange={(e) => handleFilterChange("thick", e.target.value)}
              >
                <option value="">Any Thick</option>
                {[...new Set(products.map((p) => p.thick))].map((thick) => (
                  <option key={thick} value={thick}>
                    {thick}
                  </option>
                ))}
              </select>

              {/* Thickness Tolerance */}
              <label className="block mb-2 text-base leading-5 font-bold text-white">
                Free Diameter (mm)
              </label>
              <select
                className="w-full p-2 border rounded mb-4 text-sm text-[#999]"
                onChange={(e) =>
                  handleFilterChange("free_diameter", e.target.value)
                }
              >
                <option value="">Any Free Diameter</option>
                {[...new Set(products.map((p) => p.free_diameter))].map(
                  (tolerance) => (
                    <option key={tolerance} value={tolerance}>
                      {tolerance}
                    </option>
                  )
                )}
              </select>

              {/* Thickness Tolerance */}
              <label className="block mb-2 text-base leading-5 font-bold text-white">
                Beam (mm)
              </label>
              <select
                className="w-full p-2 border rounded mb-4 text-sm text-[#999]"
                onChange={(e) => handleFilterChange("beam", e.target.value)}
              >
                <option value="">Any Beam</option>
                {[...new Set(products.map((p) => p.beam))].map(
                  (tolerance, index) => (
                    <option key={index} value={tolerance}>
                      {tolerance}
                    </option>
                  )
                )}
              </select>

              {/* Thickness Tolerance */}
              <label className="block mb-2 text-base leading-5 font-bold text-white">
                Hole Diameter (mm)
              </label>
              <select
                className="w-full p-2 border rounded mb-4 text-sm text-[#999]"
                onChange={(e) =>
                  handleFilterChange("hole_diameter", e.target.value)
                }
              >
                <option value="">Any Hole Diameter</option>
                {[...new Set(products.map((p) => p.hole_diameter))].map(
                  (tolerance, index) => (
                    <option key={index} value={tolerance}>
                      {tolerance}
                    </option>
                  )
                )}
              </select>

              {/* Thickness Tolerance */}
              <label className="block mb-2 text-base leading-5 font-bold text-white">
                Lug
              </label>
              <select
                className="w-full p-2 border rounded mb-4 text-sm text-[#999]"
                onChange={(e) => handleFilterChange("lug", e.target.value)}
              >
                <option value="">Any Lug</option>
                {[...new Set(products.map((p) => p.lug))].map((tolerance) => (
                  <option key={tolerance} value={tolerance}>
                    {tolerance}
                  </option>
                ))}
              </select>

              {/* Thickness Tolerance */}
              <label className="block mb-2 text-base leading-5 font-bold text-white">
                Groove Width (mm)
              </label>
              <select
                className="w-full p-2 border rounded mb-4 text-sm text-[#999]"
                onChange={(e) =>
                  handleFilterChange("groove_width", e.target.value)
                }
              >
                <option value="">Any Groove width</option>
                {[...new Set(products.map((p) => p.groove_width))].map(
                  (tolerance) => (
                    <option key={tolerance} value={tolerance}>
                      {tolerance}
                    </option>
                  )
                )}
              </select>

              {/* Thickness Tolerance */}
              <label className="block mb-2 text-base leading-5 font-bold text-white">
                Groove Diameter (mm)
              </label>
              <select
                className="w-full p-2 border rounded mb-4 text-sm text-[#999]"
                onChange={(e) =>
                  handleFilterChange("groove_diameter", e.target.value)
                }
              >
                <option value="">Any Groove Diameer</option>
                {[...new Set(products.map((p) => p.groove_diameter))].map(
                  (tolerance) => (
                    <option key={tolerance} value={tolerance}>
                      {tolerance}
                    </option>
                  )
                )}
              </select>

              {/* Reset Filters */}
              <button
                className="w-full mt-4 bg-red-500 text-white py-2 rounded hover:bg-red-600 text-sm"
                onClick={resetFilters}
              >
                Reset Filters
              </button>

              {/* Info Box */}
              <div className="w-full mt-8 text-white">
                <h2 className="text-xl font-semibold mb-4">
                  For Regular Customer
                </h2>
                <p className="mb-3">
                  Thank you for your continued interest in our products!
                </p>
                <p className="mb-3">
                  To stay updated on the latest product releases, technical
                  insights, and exclusive offers, we invite you to connect with
                  us by sharing your email.
                </p>
                <ul className="mb-4 list-disc list-inside space-y-1">
                  <li className="border-b pb-1">New product announcements</li>
                  <li className="border-b pb-1">
                    Technical datasheets and usage tips
                  </li>
                  <li className="border-b pb-1">
                    Special offers tailored for our regular customers
                  </li>
                </ul>
                <p>
                  Please share your email with us at{" "}
                  <span className="bg-[#ef3f33] px-2 py-1 rounded inline-block break-all">
                    info@supersprings.co.in
                  </span>{" "}
                  and we’ll make sure you’re always informed and ahead.
                </p>
              </div>
            </div>

            {/* Updated table section with responsive design */}
            <div className="w-full sm:w-[81%]">
              {/* start product detail */}
              <SingleCategory />
              {/* end product detail */}
              {/* start shorting */}
              {/* New table controls with responsive design */}
              <div className="flex flex-wrap justify-between items-center mb-4 px-2 gap-3 py-2 border-b">
                <div className="w-full flex flex-wrap justify-between items-center space-x-2 py-2 px-2 sm:space-x-4 bg-gray-300">
                  <span className="text-xl text-black font-bold">
                    Display Price:
                  </span>
                  <div className="flex rounded-md shadow-sm">
                    <button
                      onClick={() => handlePriceDisplayChange("per100")}
                      className={`px-2 sm:px-4 py-1 sm:py-2 text-[1.1rem] sm:text-sm font-medium border ${priceDisplay === "per100"
                        ? "bg-[#ef3f33] text-white border-[#ef3f33]"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                        } rounded-l-md focus:outline-none`}
                    >
                      Per 100
                    </button>
                    <button
                      onClick={() => handlePriceDisplayChange("single")}
                      className={`px-2 sm:px-4 py-1 sm:py-2 text-[1.1rem] sm:text-sm font-medium border-t border-b border-r ${priceDisplay === "single"
                        ? "bg-[#ef3f33] text-white border-[#ef3f33]"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                        } rounded-r-md focus:outline-none`}
                    >
                      Per unit
                    </button>
                  </div>
                </div>
                <div className="flex items-center space-x-2 sm:space-x-4 mb-2 sm:mb-0">
                  <span className="text-sm sm:text-base text-white">Show</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    className="border rounded p-1 text-sm"
                  >
                    {[10, 25, 50, 100, "All"].map((value) => (
                      <option
                        key={value}
                        value={
                          value === "All" ? searchedProducts.length : value
                        }
                      >
                        {value}
                      </option>
                    ))}
                  </select>
                  <span className="text-sm sm:text-base text-white">
                    entries
                  </span>
                </div>

                <div className="w-full sm:w-auto flex flex-wrap items-center space-y-2 sm:space-y-0 sm:space-x-4">
                  {/* Existing search and currency controls */}
                  <div className="w-full sm:w-auto flex-grow flex justify-center mb-2 sm:mb-0">
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="border border-gray-300 p-2 sm:p-2 rounded-lg w-full shadow-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                  <div className="w-1/2 sm:w-auto">
                    <select
                      value={currency}
                      onChange={handleCurrencyChange}
                      className="w-full bg-gray-100 border border-gray-300 text-gray-700 p-2 sm:p-2 rounded-lg shadow-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="RUPEE">Rupee (₹)</option>
                      <option value="DOLLAR">Dollar ($)</option>
                      <option value="EURO">Euro (€)</option>
                    </select>
                  </div>
                  <div className="w-1/2 sm:w-auto text-right">
                    <Link
                      to="/basket"
                      className="inline-block bg-[#ef3f33] text-white px-4 sm:px-6 py-2 sm:py-2 rounded-lg hover:bg-green-600 shadow-md"
                    >
                      View Selected Cart
                    </Link>
                  </div>
                </div>
              </div>

              {/* end shorting */}
              // In Table2.js, update the ProductTable call:
              <ProductTable
                currentProducts={currentProducts}
                expandedRow={expandedRow}
                toggleExpandRow={toggleExpandRow}
                getCurrencySymbol={getCurrencySymbol}
                calculatePrice={calculatePrice}
                counters={counters}
                handleInputChange={handleInputChange}
                increment={increment}
                decrement={decrement}
                openPopup={openPopup}
                selectedItems={selectedItems}
                handleSelectAll={handleSelectAll}
                handleSelectItem={handleSelectItem}
                shopnow={shopnow}
                priceDisplay={priceDisplay}  // <-- Add this
                getPriceForQuantity={getPriceForQuantity}
              />

              {/* Pagination controls with responsive design */}
              <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm">
                <div className="mb-2 sm:mb-0">
                  Showing {startIndex + 1} to{" "}
                  {Math.min(endIndex, searchedProducts.length)} of{" "}
                  {searchedProducts.length} entries
                </div>
                <div className="flex flex-wrap justify-center space-x-1 sm:space-x-2">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className="px-2 py-1 border rounded disabled:opacity-50 text-xs sm:text-sm"
                  >
                    Prev
                  </button>

                  {/* Simplified pagination for mobile */}
                  {currentPage > 2 && (
                    <>
                      <button
                        onClick={() => setCurrentPage(1)}
                        className="px-2 py-1 border rounded text-xs sm:text-sm"
                      >
                        1
                      </button>
                      {currentPage > 3 && (
                        <span className="px-1 py-1 text-xs sm:text-sm">
                          ...
                        </span>
                      )}
                    </>
                  )}

                  {currentPage > 1 && (
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      className="px-2 py-1 border rounded text-xs sm:text-sm"
                    >
                      {currentPage - 1}
                    </button>
                  )}

                  <button className="px-2 py-1 border rounded bg-blue-500 text-white text-xs sm:text-sm">
                    {currentPage}
                  </button>

                  {currentPage < totalPages && (
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      className="px-2 py-1 border rounded text-xs sm:text-sm"
                    >
                      {currentPage + 1}
                    </button>
                  )}

                  {currentPage < totalPages - 1 && (
                    <>
                      {currentPage < totalPages - 2 && (
                        <span className="px-1 py-1 text-xs sm:text-sm">
                          ...
                        </span>
                      )}
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        className="px-2 py-1 border rounded text-xs sm:text-sm"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}

                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="px-2 py-1 border rounded disabled:opacity-50 text-xs sm:text-sm"
                  >
                    Next
                  </button>
                </div>
              </div>

              {/* Bulk action button with responsive design */}
              {selectedItems.length > 0 && (
                <div className="mt-4">
                  <button
                    onClick={addSelectedToCart}
                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 w-full sm:w-auto text-sm"
                  >
                    Add Selected Items ({selectedItems.length})
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Table2;
